import { getUserTypeId } from "../../../utils";
import React, { useContext, useEffect, useCallback } from "react";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../slices/store";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Col, Row } from "react-bootstrap";
import { getSingleUser } from "../../../slices/users/userManagementSlice";
import { handleEditCustomer } from "../service/clientService";
import { TodosContext } from "../../../context";
import { STATE_VALUES } from "./constants";
import { clientsGet } from "../../../slices/clients/clientManagementSlice";
import useRequestSettings from "../../../hooks/useRequestClient";
import UseAuthConfig from "../../../hooks/useAuthConfig";
import Headline from "../../../components/organisms/Headline/Headline";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import LabelInsideInput from "../../../components/molecules/InputController/LabelInsideInputField";
import LabelInsideSelectField from "../../../components/molecules/InputController/LabelInsideSelectField";
import Loader from "../../../components/atoms/Loader/Loader";
import { isEqual } from "lodash";
import {
  cleanPhoneNumberInput,
  validatePhoneNumber,
} from "../../../utils/validationUtils";

const ClientEdit = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { addAlert } = useContext(TodosContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isChanged, setIsChanged] = React.useState(false);

  const selectedUser = useSelector(
    (state: RootState) => state.userManagement.selectedUser
  );

  const [reqRequests] = useRequestSettings();
  const { protectedResources }: any = UseAuthConfig();
  const endpoint = protectedResources.apiTodoList.endpoint;

  /** -----------------------------
   * Fetch user by ID
   * ----------------------------- */
  useEffect(() => {
    const pathParts = location.pathname.split("/");
    if (pathParts.length === 4) {
      const id = pathParts[3];
      const accessToken = localStorage.getItem("AccessToken") || "";
      const url = `${endpoint}/users/${id}`;
      dispatch(getSingleUser({ accessToken, url }));
    }
  }, [location.pathname, dispatch, endpoint]);

  /** -----------------------------
   * Build original values object
   * ----------------------------- */
  const getOriginalValues = useCallback(() => {
    if (!selectedUser) return null;

    return {
      firstName: selectedUser.firstName ?? "",
      lastName: selectedUser.lastName ?? "",
      email: selectedUser.email ?? "",
      mobileNumber: selectedUser.mobileNumber ?? "",
      state: selectedUser.state
        ? { value: selectedUser.state, label: selectedUser.state }
        : null,
      jobTitle: selectedUser.jobTitle ?? "",
      companyName: selectedUser.companyName ?? "",
    };
  }, [selectedUser]);

  /** -----------------------------
   * Formik setup
   * ----------------------------- */
  const formik = useFormik({
    initialValues: getOriginalValues() || {
      firstName: "",
      lastName: "",
      email: "",
      mobileNumber: "",
      state: null,
      jobTitle: "",
      companyName: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      email: Yup.string().required("Please enter email"),
      mobileNumber: Yup.string()
        .required("Please enter mobile number")
        .test("phone-valid", "Invalid phone number", (value) => {
          if (!value) return false;
          const result = validatePhoneNumber(value);
          return result.isValid;
        }),
      state: Yup.object().nullable().required("Please select a state"),
    }),
    onSubmit: (values) => {
      if (!selectedUser) return;

      // Compare original vs current
      if (isEqual(values, getOriginalValues())) {
        addAlert("No changes detected", "warning", true);
        return;
      }

      setIsLoading(true);

      const stateValue =
        typeof values?.state?.value === "number"
          ? values.state.value
          : values?.state?.value === "Active"
          ? 2
          : 3;

      const payload = {
        ...selectedUser,
        ...values,
        state: stateValue,
        type: getUserTypeId(selectedUser.type),
      };

      const url = `${protectedResources.apiTodoList.endpoint}/users/${selectedUser.id}`;

      handleEditCustomer(
        payload,
        dispatch,
        setIsLoading,
        addAlert,
        true,
        navigate,
        protectedResources,
        url,
        reqRequests,
        clientsGet
      );
    },
  });

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = cleanPhoneNumberInput(e.target.value);

    // Count digits and limit to 15
    const digits = cleaned.replace(/\D/g, "");
    if (digits.length > 15) return;

    formik.setFieldValue("mobileNumber", cleaned);
  };

  /** -----------------------------
   * Validate on blur
   * ----------------------------- */
  const handleMobileBlur = () => {
    formik.setFieldTouched("mobileNumber", true);
    const result = validatePhoneNumber(formik.values.mobileNumber);
    if (!result.isValid) {
      formik.setFieldError("mobileNumber", result.message);
    }
  };

  /** -----------------------------
   * Detect if form is changed
   * ----------------------------- */
  useEffect(() => {
    if (!selectedUser) return;

    const original = getOriginalValues();
    const changed = !isEqual(formik.values, original);
    setIsChanged(changed);
  }, [formik.values, selectedUser, getOriginalValues]);

  /** -----------------------------
   * UI
   * ----------------------------- */
  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline name="Edit Customer" />
      <div className="box-container">
        <div className="d-flex">
          <form onSubmit={formik.handleSubmit} id="client-edit-form">
            {/* First row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="firstName"
                  label="First Name"
                  {...formik.getFieldProps("firstName")}
                  error={formik.errors.firstName}
                  touched={formik.touched.firstName}
                  showLabel={false}
                />
              </Col>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="lastName"
                  label="Last Name"
                  {...formik.getFieldProps("lastName")}
                  error={formik.errors.lastName}
                  touched={formik.touched.lastName}
                  showLabel={false}
                />
              </Col>
            </Row>

            {/* Second row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="email"
                  label="Email"
                  value={formik.values.email}
                  error={formik.errors.email}
                  touched={formik.touched.email}
                  showLabel={false}
                  disabled={true}
                />
              </Col>

              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="mobileNumber"
                  label="Mobile Number"
                  {...formik.getFieldProps("mobileNumber")}
                  onChange={handleMobileChange}
                  onBlur={handleMobileBlur}
                  error={formik.errors.mobileNumber}
                  touched={formik.touched.mobileNumber}
                  showLabel={false}
                />
              </Col>
            </Row>

            {/* Third row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideSelectField
                  id="state"
                  name="state"
                  label="State"
                  value={formik.values.state}
                  onChange={(v) => formik.setFieldValue("state", v)}
                  onBlur={() => formik.setFieldTouched("state", true)}
                  options={STATE_VALUES}
                  error={formik.errors}
                  touched={formik.touched}
                  showLabel={false}
                />
              </Col>

              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="jobTitle"
                  label="Job Title"
                  {...formik.getFieldProps("jobTitle")}
                  error={formik.errors.jobTitle}
                  touched={formik.touched.jobTitle}
                  showLabel={false}
                />
              </Col>
            </Row>

            {/* Fourth row */}
            <Row>
              <Col xs={12} sm={6}>
                <LabelInsideInput
                  id="companyName"
                  label="Company Name"
                  {...formik.getFieldProps("companyName")}
                  error={formik.errors.companyName}
                  touched={formik.touched.companyName}
                  showLabel={false}
                />
              </Col>
            </Row>
          </form>

          {isLoading ? <Loader /> : null}
        </div>
      </div>

      <div className="footer-btn-section d-flex justify-content-end box-container gap-3">
        <button
          className="btn btn-secondary"
          type="button"
          onClick={() => navigate(-1)}
        >
          Back
        </button>

        <button
          className="btn btn-primary"
          type="submit"
          form="client-edit-form"
          disabled={!isChanged || isLoading}
          style={{
            opacity: !isChanged || isLoading ? 0.6 : 1,
            cursor: !isChanged || isLoading ? "not-allowed" : "pointer",
          }}
        >
          Submit
        </button>
      </div>
    </Container>
  );
};

export default ClientEdit;
